import { useContext } from "react";
import { useTranslation } from "react-i18next";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "@tanstack/react-router";
import { apiClient } from "../../../../zodios/client";
import type { AdPropertyUpdate } from "../../../../zodios/apiDefinitions/api/ads";
import { useAppSelector } from "../../../../redux/hooks";
import { AdPlanContext } from "./AdPlanContext";
import { roasToStoredAcos } from "./utils";
import { AD_STATES } from "./constants";
import { toAdPlanData } from "./toAdPlanData";
import type { AdPlan, ToAdPlanDataContext } from "./types";
import { successToast, errorToast } from "../../../../utils/toasts";

interface SavedChildPlan {
  id?: number;
  name?: string;
  allocation?: number | string;
  targetACOS?: number | string;
  maxACOS?: number | string;
  targetAcosFromServer?: number | string | null;
  maxAcosFromServer?: number | string | null;
  categoryId?: number | null;
  [key: string]: unknown;
}

export function useUpdateAdPlan({ adId }: { adId?: number } = {}) {
  const { t } = useTranslation(["advertising"]);
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const { savedChildPlans, setRefetchOnUpdate } = useContext(AdPlanContext);
  const marketPlace = useAppSelector((state) => state.marketPlace.marketPlace);
  const user = useAppSelector((state) => state.user);

  const ctx: ToAdPlanDataContext = {
    adId,
    marketPlace,
    acosOrRoas: user.preferences.acos_or_roas,
    organizationType: user.organization_type ?? "",
  };

  return useMutation({
    mutationFn: async ({ adPlan }: { adPlan: AdPlan }) => {
      const children: SavedChildPlan[] = (savedChildPlans ?? []) as SavedChildPlan[];

      const childData = children
        .filter((c): c is SavedChildPlan & { id: number } => typeof c.id === "number")
        .map((c) => ({
          id: c.id,
          name: c.name,
          budget_allocation: parseInt(c.allocation as string),
          target_acos:
            user.preferences.acos_or_roas === "roas" ?
              roasToStoredAcos(c.targetACOS, c.targetAcosFromServer)
            : parseFloat(c.targetACOS as string),
          max_acos:
            user.preferences.acos_or_roas === "roas" ?
              roasToStoredAcos(c.maxACOS, c.maxAcosFromServer)
            : parseFloat(c.maxACOS as string),
        }));

      const data = toAdPlanData(adPlan, ctx);
      if (!data) throw new Error("No ad plan data to submit");

      const body: AdPropertyUpdate = {
        ...data,
        id: (adId ?? adPlan.id) as number,
        custom_api_request: "update_advertisement",
        child_data: childData,
      };
      await apiClient.updateAdProperties(body);

      // Create annotation if provided
      if (adPlan.adUpdateAnnotation?.length) {
        await apiClient.createAnnotation({
          marketplace: marketPlace,
          description: adPlan.adUpdateAnnotation,
          advertisement: adPlan.id,
          ts: new Date().toISOString(),
        });
        void queryClient.invalidateQueries({ queryKey: [{ entity: "Annotations" }] });
      }

      // Create unsaved children
      const parent = adPlan.id;
      for (const child of children) {
        if (!child.id) {
          const childAdplan: AdPlan = {
            ...(child as AdPlan),
            parent,
            categoryId: adPlan.categoryId,
          };
          const childPayload = toAdPlanData(childAdplan, ctx);
          if (childPayload) {
            await apiClient.createAdvertisement(childPayload);
          }
        }
      }

      return { state: data.state, category: data.category };
    },

    onSuccess: ({ state, category }) => {
      void queryClient.invalidateQueries();
      setRefetchOnUpdate(true);
      successToast({ message: t(($) => $.advertising["Updating Media Plan"]) });

      if (state === AD_STATES.ARCHIVE.value) {
        void navigate({ to: "/user/advertising/dashboard/category/$id", params: { id: String(category) } });
      }
    },

    onError: (error) => {
      errorToast({ message: t(($) => $.advertising["Failed to Update Media Plan"]), error });
    },
  });
}
